/**
 * GardenWarden Direction B hero — turret + wireframe birds + water stream.
 *
 * File is CAD Z-up. orientRoot uses Euler YXZ (y=+90°, x=-90°) so +Z→+Y up and
 * spray −X→+Z toward camera. (XYZ −90/90 is wrong in three.js — tumbles the unit.)
 * Dome + FixedBase pan with the arms; tilt head aims elevation. CAD pivots:
 * pan = rotation.z (vertical), tilt = rotation.y (elevation). Water from spray.
 */
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";

const MINT = 0x7dffb3;
const AMBER = 0xf0a030;

/** No upright-only shell: dome + base pan with the arms (Ford 2026-09-28). */
const STATIC_NAMES = new Set([]);
/** Aiming head — pans with turret and tilts for elevation. */
const TILT_NAMES = new Set([
  "tiltgear",
  "u-mountleft",
  "u-mountright",
  "cammount",
  "sprayholebinder",
  "lidbody",
  "lidbinder",
  "mountpeg",
  "servo-sg90-final",
  "servobinder",
  "pumpfrontbinder",
  "pivotpointbinder",
  "enclosurebasebinder",
  "tiltservomountbinder",
]);
const TILT_PREFIXES = ["tiltgear", "u-mount", "cammount", "sprayhole", "lidbody", "mountpeg", "servo", "pumpfront", "pivotpoint", "tiltservo"];

function prefersReducedMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
}

function makeBirdGeometry() {
  // Simple glide silhouette (body + short wings), facing +Z. No articulated flap.
  const pts = [
    0, 0, 0.045, 0.018, 0, 0, 0, 0, -0.028, -0.018, 0, 0, 0, 0, 0.045,
    -0.018, 0, 0, -0.055, 0, -0.005, -0.018, 0, -0.015,
    0.018, 0, 0, 0.055, 0, -0.005, 0.018, 0, -0.015,
    0, 0, 0.045, 0, 0, 0.062,
  ];
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
  return geo;
}

function createBird(color) {
  const mat = new THREE.LineBasicMaterial({
    color,
    transparent: true,
    opacity: 0.88,
  });
  const bird = new THREE.Line(makeBirdGeometry(), mat);
  bird.userData.phase = Math.random() * Math.PI * 2;
  bird.userData.radius = 0.38 + Math.random() * 0.18;
  bird.userData.height = 0.08 + Math.random() * 0.1;
  bird.userData.speed = 0.22 + Math.random() * 0.15;
  bird.userData.bob = 0.55 + Math.random() * 0.35;
  return bird;
}

/** CAD Dome is a double wall: Dome/Dome_3 outer, Dome_6/7 inner, Dome_5 bottom cap.
 *  Inner + cap + DoubleSide near-white glass → white flash + join rim artifact. */
function isDomeMeshName(name) {
  const n = (name || "").toLowerCase();
  return n === "dome" || n === "dome_shell" || /^dome_\d+$/.test(n);
}

function makeDomeGlassMaterial() {
  // Saturated mint (NOT near-white 0xe8fff6). Outer shell only.
  // FrontSide avoids StandardMaterial backface lighting washing to white;
  // depthWrite kills transparent sort flash.
  return new THREE.MeshStandardMaterial({
    color: 0x4cbf8c,
    metalness: 0.0,
    roughness: 0.62,
    transparent: true,
    opacity: 0.9,
    depthWrite: true,
    depthTest: true,
    side: THREE.FrontSide,
    emissive: new THREE.Color(0x0a3324),
    emissiveIntensity: 0.1,
    vertexColors: false,
  });
}

function domeMeanXYRadius(geometry) {
  const pos = geometry.attributes.position;
  if (!pos || !pos.count) return 0;
  let cx = 0;
  let cy = 0;
  const n = pos.count;
  for (let i = 0; i < n; i++) {
    cx += pos.getX(i);
    cy += pos.getY(i);
  }
  cx /= n;
  cy /= n;
  let r = 0;
  for (let i = 0; i < n; i++) {
    const dx = pos.getX(i) - cx;
    const dy = pos.getY(i) - cy;
    r += Math.hypot(dx, dy);
  }
  return r / n;
}

/** Drop CAD inner wall + bottom cap. Keep outer cyl/hemi (+ small cutout prims).
 *  Three.js names Dome_1…Dome_9 are 1-based and do not match FreeCAD geom indices,
 *  so classify by bounds/radius — not by name suffix. */
function stripDomeInnerShells(root) {
  const meshes = [];
  root.traverse((o) => {
    if (o.isMesh && isDomeMeshName(o.name)) meshes.push(o);
  });
  if (!meshes.length) return 0;

  const meta = meshes.map((o) => {
    const g = o.geometry;
    if (!g.boundingBox) g.computeBoundingBox();
    const bb = g.boundingBox;
    const zSpan = bb.max.z - bb.min.z;
    const xySpan = Math.max(bb.max.x - bb.min.x, bb.max.y - bb.min.y);
    return {
      o,
      verts: g.attributes.position?.count || 0,
      zMin: bb.min.z,
      zMax: bb.max.z,
      zSpan,
      xySpan,
      radius: domeMeanXYRadius(g),
      flatCap: zSpan < 1e-3 && xySpan > 0.15,
      cylBand: bb.min.z < -0.05 && bb.max.z < 0.02 && zSpan > 0.05,
      hemiBand: bb.min.z > -0.01 && bb.max.z > 0.08 && zSpan > 0.05,
    };
  });

  const doomed = new Set();
  for (const m of meta) {
    if (m.flatCap) doomed.add(m.o);
  }

  const cyls = meta.filter((m) => m.cylBand && m.verts > 200);
  if (cyls.length >= 2) {
    cyls.sort((a, b) => b.radius - a.radius);
    for (const m of cyls.slice(1)) doomed.add(m.o); // keep largest-radius = outer
  }

  const hemis = meta.filter((m) => m.hemiBand && m.verts > 1000);
  if (hemis.length >= 2) {
    hemis.sort((a, b) => b.radius - a.radius);
    for (const m of hemis.slice(1)) doomed.add(m.o);
  }

  for (const o of doomed) {
    o.parent?.remove(o);
    o.geometry?.dispose?.();
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    mats.forEach((m) => m?.dispose?.());
  }
  return doomed.size;
}

/** Merge outer cylinder + hemisphere into one mesh so the CAD join ring disappears. */
function mergeDomeOuterShell(root) {
  const candidates = [];
  root.traverse((o) => {
    if (!o.isMesh || !isDomeMeshName(o.name)) return;
    const g = o.geometry;
    if (!g.boundingBox) g.computeBoundingBox();
    const bb = g.boundingBox;
    const verts = g.attributes.position?.count || 0;
    const cyl = bb.min.z < -0.05 && bb.max.z < 0.02 && verts > 200;
    const hemi = bb.min.z > -0.01 && bb.max.z > 0.08 && verts > 1000;
    if (cyl || hemi) candidates.push({ o, cyl, hemi, radius: domeMeanXYRadius(g) });
  });
  const cyls = candidates.filter((c) => c.cyl).sort((a, b) => b.radius - a.radius);
  const hemis = candidates.filter((c) => c.hemi).sort((a, b) => b.radius - a.radius);
  if (!cyls.length || !hemis.length) return false;
  const cyl = cyls[0].o;
  const hemi = hemis[0].o;
  if (cyl.parent !== hemi.parent) return false;

  cyl.updateMatrixWorld(true);
  hemi.updateMatrixWorld(true);
  const parent = cyl.parent;
  parent.updateMatrixWorld(true);
  const parentInv = parent.matrixWorld.clone().invert();

  const g1 = cyl.geometry.clone();
  g1.applyMatrix4(new THREE.Matrix4().multiplyMatrices(parentInv, cyl.matrixWorld));
  const g2 = hemi.geometry.clone();
  g2.applyMatrix4(new THREE.Matrix4().multiplyMatrices(parentInv, hemi.matrixWorld));

  const merged = new THREE.BufferGeometry();
  // manual merge without BufferGeometryUtils
  const p1 = g1.attributes.position;
  const p2 = g2.attributes.position;
  const n1 = g1.attributes.normal;
  const n2 = g2.attributes.normal;
  const vCount = p1.count + p2.count;
  const pos = new Float32Array(vCount * 3);
  const nor = new Float32Array(vCount * 3);
  for (let i = 0; i < p1.count; i++) {
    pos[i * 3] = p1.getX(i); pos[i * 3 + 1] = p1.getY(i); pos[i * 3 + 2] = p1.getZ(i);
    if (n1) { nor[i * 3] = n1.getX(i); nor[i * 3 + 1] = n1.getY(i); nor[i * 3 + 2] = n1.getZ(i); }
  }
  for (let i = 0; i < p2.count; i++) {
    const j = p1.count + i;
    pos[j * 3] = p2.getX(i); pos[j * 3 + 1] = p2.getY(i); pos[j * 3 + 2] = p2.getZ(i);
    if (n2) { nor[j * 3] = n2.getX(i); nor[j * 3 + 1] = n2.getY(i); nor[j * 3 + 2] = n2.getZ(i); }
  }
  merged.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  merged.setAttribute("normal", new THREE.BufferAttribute(nor, 3));

  const i1 = g1.index;
  const i2 = g2.index;
  let idx;
  if (i1 && i2) {
    idx = new Uint32Array(i1.count + i2.count);
    for (let i = 0; i < i1.count; i++) idx[i] = i1.getX(i);
    for (let i = 0; i < i2.count; i++) idx[i1.count + i] = i2.getX(i) + p1.count;
  } else {
    idx = null;
  }
  if (idx) merged.setIndex(new THREE.BufferAttribute(idx, 1));

  // Weld near-duplicate verts at the cyl/hemi join so the seam ring does not z-fight
  {
    const EPS = 1.2e-3;
    const attr = merged.attributes.position;
    const map = new Map();
    const remap = new Int32Array(attr.count);
    const keyOf = (i) => {
      const x = Math.round(attr.getX(i) / EPS);
      const y = Math.round(attr.getY(i) / EPS);
      const z = Math.round(attr.getZ(i) / EPS);
      return `${x},${y},${z}`;
    };
    for (let i = 0; i < attr.count; i++) {
      const k = keyOf(i);
      if (map.has(k)) remap[i] = map.get(k);
      else {
        map.set(k, i);
        remap[i] = i;
      }
    }
    if (merged.index) {
      const ia = merged.index.array;
      for (let i = 0; i < ia.length; i++) ia[i] = remap[ia[i]];
      merged.index.needsUpdate = true;
    }
    merged.computeVertexNormals();
  }

  merged.computeBoundingBox();
  merged.computeBoundingSphere();

  const mesh = new THREE.Mesh(merged, makeDomeGlassMaterial());
  mesh.name = "Dome_shell";
  mesh.renderOrder = 3;
  parent.add(mesh);

  for (const o of [cyl, hemi]) {
    o.parent?.remove(o);
    o.geometry?.dispose?.();
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    mats.forEach((m) => m?.dispose?.());
  }
  g1.dispose();
  g2.dispose();
  return true;
}

function styleTurretMaterials(root) {
  root.traverse((obj) => {
    if (!obj.isMesh || !obj.material) return;
    const n = (obj.name || "").toLowerCase();
    const baseName = n.replace(/_\d+$/, "");
    const isDomeGlass = isDomeMeshName(n);
    const isDomeLattice = /^(groove|revolution)/.test(baseName);

    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    const next = mats.map(() => {
      if (isDomeGlass) {
        return makeDomeGlassMaterial();
      }
      if (isDomeLattice) {
        // Soft lattice hint only — keep solid (no wireframe) and very faint
        return new THREE.MeshStandardMaterial({
          color: 0x6a9a88,
          metalness: 0.1,
          roughness: 0.35,
          transparent: true,
          opacity: 0.04,
          depthWrite: false,
          side: THREE.DoubleSide,
          emissive: new THREE.Color(MINT),
          emissiveIntensity: 0.02,
        });
      }
      const mat = new THREE.MeshStandardMaterial({
        color: 0x3a5168,
        metalness: 0.65,
        roughness: 0.35,
        emissive: new THREE.Color(MINT),
        emissiveIntensity: 0.04,
        side: THREE.DoubleSide,
        transparent: false,
        opacity: 1,
        depthWrite: true,
      });
      if (/lid|cam|spray|pump/.test(n)) {
        mat.color.set(0x4a6578);
        mat.emissiveIntensity = 0.06;
      }
      if (/base|fixed|pan|gear|tilt|mount|servo/.test(n)) {
        mat.color.set(0x1c2836);
        mat.metalness = 0.85;
        mat.color.setHex(0x2a3848);
      }
      return mat;
    });
    obj.material = next.length === 1 ? next[0] : next;
    obj.castShadow = false;
    obj.receiveShadow = false;
    // Keep dome shell from occluding internals incorrectly
    if (isDomeGlass || isDomeLattice) obj.renderOrder = 2;
  });
}

function classifyPart(name) {
  const n = (name || "").toLowerCase();
  if (!n) return "pan";
  // Strip _123 primitive suffixes from multi-primitive meshes
  const base = n.replace(/_\d+$/, "");
  if (STATIC_NAMES.has(n) || STATIC_NAMES.has(base)) return "static";
  if (TILT_NAMES.has(n) || TILT_NAMES.has(base)) return "tilt";
  if (TILT_PREFIXES.some((p) => base === p || base.startsWith(p))) return "tilt";
  return "pan";
}

function shortestAngle(from, to) {
  let d = to - from;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

async function boot(el) {
  const canvas = el.querySelector("canvas");
  const glbUrl = el.dataset.glb;
  if (!canvas || !glbUrl) return;

  const reduced = prefersReducedMotion();
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: "high-performance",
  });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, 1, 0.01, 20);
  camera.position.set(0.28, 0.32, 0.85);
  camera.lookAt(0, 0.05, 0);

  scene.add(new THREE.AmbientLight(0x8a9aac, 0.65));
  const key = new THREE.DirectionalLight(0xffffff, 1.35);
  key.position.set(2, 4, 3);
  scene.add(key);
  const rim = new THREE.DirectionalLight(AMBER, 0.75);
  rim.position.set(-3, 2, -2);
  scene.add(rim);
  const fill = new THREE.PointLight(MINT, 0.4, 4);
  fill.position.set(0, 0.35, 0.55);
  scene.add(fill);
  // Soft fill inside dome so mechanism reads through glass
  const core = new THREE.PointLight(0xa8ffe0, 0.55, 0.55);
  core.position.set(0, 0.08, 0);
  scene.add(core);

  const ring = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints(
      Array.from({ length: 64 }, (_, i) => {
        const a = (i / 64) * Math.PI * 2;
        return new THREE.Vector3(Math.cos(a) * 0.22, -0.135, Math.sin(a) * 0.22);
      })
    ),
    new THREE.LineBasicMaterial({ color: MINT, transparent: true, opacity: 0.25 })
  );
  scene.add(ring);

  // CAD Z-up → Y-up, spray (−X) → +Z
  const orientRoot = new THREE.Group();
  // YXZ: yaw spray to camera, then pitch CAD Z-up → world Y-up
  orientRoot.rotation.order = "YXZ";
  orientRoot.rotation.y = Math.PI / 2;
  orientRoot.rotation.x = -Math.PI / 2;
  scene.add(orientRoot);

  const turretRoot = new THREE.Group();
  // Assembled in FILE space first, then parented under orientRoot (avoids double-orient on attach).

  const staticGroup = new THREE.Group();
  staticGroup.name = "staticUpright";
  turretRoot.add(staticGroup);

  // Pan around CAD Z (vertical after orient). Pivot near turret XY center.
  const panPivot = new THREE.Group();
  panPivot.name = "panPivot";
  panPivot.position.set(0.0077, 0.0115, 0);
  turretRoot.add(panPivot);

  const panGroup = new THREE.Group();
  panGroup.name = "panGroup";
  panPivot.add(panGroup);

  // Tilt around CAD Y at PivotPointBinder (file space, pre-center)
  const TILT_PIVOT_FILE = new THREE.Vector3(0.02602, -0.005, -0.024);
  const tiltPivot = new THREE.Group();
  tiltPivot.name = "tiltPivot";
  panPivot.add(tiltPivot);

  const tiltGroup = new THREE.Group();
  tiltGroup.name = "tiltGroup";
  tiltPivot.add(tiltGroup);

  const sprayMarker = new THREE.Object3D();
  sprayMarker.position.set(-0.1077, 0.014, 0.0005);
  tiltGroup.add(sprayMarker);

  const waterPositions = new Float32Array(2 * 3);
  const waterGeo = new THREE.BufferGeometry();
  waterGeo.setAttribute("position", new THREE.BufferAttribute(waterPositions, 3));
  const waterLine = new THREE.Line(
    waterGeo,
    new THREE.LineBasicMaterial({
      color: MINT,
      transparent: true,
      opacity: 0,
      depthTest: true,
    })
  );
  scene.add(waterLine);

  const dropletGeo = new THREE.BufferGeometry();
  const dropletCount = 12;
  const dropletPos = new Float32Array(dropletCount * 3);
  dropletGeo.setAttribute("position", new THREE.BufferAttribute(dropletPos, 3));
  const droplets = new THREE.Points(
    dropletGeo,
    new THREE.PointsMaterial({
      color: MINT,
      size: 0.012,
      transparent: true,
      opacity: 0,
      sizeAttenuation: true,
      depthWrite: false,
    })
  );
  scene.add(droplets);

  const birds = [];
  const birdColors = [MINT, AMBER, MINT, 0xa8ffe0];
  for (let i = 0; i < 4; i++) {
    const b = createBird(birdColors[i % birdColors.length]);
    b.userData.phase = (i / 4) * Math.PI * 2;
    scene.add(b);
    birds.push(b);
  }

  const draco = new DRACOLoader();
  draco.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
  draco.setDecoderConfig({ type: "js" });
  const loader = new GLTFLoader();
  loader.setDRACOLoader(draco);

  const readout = el.querySelector("[data-gwb-readout]");
  const setReadout = (target, spray) => {
    if (!readout) return;
    readout.innerHTML = `target <b>${target}</b><br>spray <span class="gwb__amber">${spray}</span><br>mode <b>yard</b>`;
  };

  let modelReady = false;
  let sprayHole = null;

  try {
    const gltf = await loader.loadAsync(glbUrl);
    const model = gltf.scene;
    // FreeCAD exports the FULL feature tree as meshes. Pad/Pocket/Chamfer/Body*
    // are intermediate solids that recreate a complete turret at rest pose.
    // They land in panGroup (unlabeled → pan) so they yaw but never tilt —
    // visible as a frozen full-body ghost while the real head aims.
    // Keep ONLY named product parts; strip all CAD history + construction.
    const KEEP_EXACT = new Set([
      // static foot / shell
      "dome",
      "fixedbase",
      "fixedsubbase",
      "basebinder",
      "base",
      // pan
      "pangear",
      // tilt / aiming assembly
      "tiltgear",
      "u-mountleft",
      "u-mountright",
      "cammount",
      "sprayholebinder",
      "lidbody",
      "lidbinder",
      "mountpeg",
      "servo-sg90-final",
      "servobinder",
      "pumpfrontbinder",
      "pivotpointbinder",
      "enclosurebasebinder",
      "tiltservomountbinder",
      "servohornbinder",
    ]);
    const KEEP_PREFIX = ["servo-"]; // Servo-9g-*, Servo-sg90-*, etc.
    const STRIP_EXACT = new Set([
      "clone",
      "groove",
      "revolution",
      "revolution001",
    ]);
    const STRIP_PREFIX = [
      // FreeCAD construction
      "origin",
      "sketch",
      "x-axis",
      "y-axis",
      "z-axis",
      "xy-plane",
      "xz-plane",
      "yz-plane",
      "datump",
      "datumplane",
      "datumpoint",
      "rotationdims",
      "involutegear",
      // FreeCAD feature history (full-body ghosts)
      "pad",
      "pocket",
      "chamfer",
      "fillet",
      "body",
      "boolean",
      "binder", // Binder### / Binder alone — not *Binder product names (those kept via KEEP)
      "copy",
      "basefeature",
      "cutoutbinder",
      "outerbinder",
    ];
    function normName(name) {
      return (name || "").toLowerCase().replace(/_\d+$/, "");
    }
    function isKeep(name) {
      const n = normName(name);
      if (!n) return false;
      if (KEEP_EXACT.has(n)) return true;
      return KEEP_PREFIX.some((p) => n === p || n.startsWith(p));
    }
    function shouldStrip(name) {
      const n = normName(name);
      if (!n) return true; // unlabeled → drop
      if (isKeep(n)) return false;
      if (STRIP_EXACT.has(n)) return true;
      // "binder033" etc. — strip numbered binders; product *Binder kept above
      if (/^binder\d+$/.test(n)) return true;
      return STRIP_PREFIX.some((p) => n === p || n.startsWith(p));
    }
    function disposeObject(obj) {
      obj.traverse((c) => {
        if (c.isMesh || c.isLine || c.isPoints) {
          c.geometry?.dispose?.();
          const mats = Array.isArray(c.material) ? c.material : [c.material];
          mats.forEach((m) => m?.dispose?.());
        }
      });
    }
    // Collect deepest-first so parents can leave after children are gone.
    const doomed = [];
    model.traverse((o) => {
      if (o === model) return;
      if (shouldStrip(o.name)) doomed.push(o);
    });
    doomed.reverse();
    for (const o of doomed) {
      o.parent?.remove(o);
      disposeObject(o);
    }
    styleTurretMaterials(model);
    const strippedDomeInner = stripDomeInnerShells(model);
    const mergedDomeShell = mergeDomeOuterShell(model);

    // Pivots stay in FILE space; meshes are not pre-centered.
    // tiltPivot is child of panPivot → pan-local coords.
    tiltPivot.position.copy(TILT_PIVOT_FILE).sub(panPivot.position);
    sprayMarker.position
      .set(-0.1077, 0.014, 0.0005)
      .sub(panPivot.position)
      .sub(tiltPivot.position);

    // Assemble under scene (identity) so attach() keeps FILE-space locals.
    // Orientation is applied once when turretRoot is added to orientRoot.
    scene.add(turretRoot);
    turretRoot.add(model);
    model.updateMatrixWorld(true);

    // Flat crowblaster: classify each direct child by its OWN name only.
    // Do NOT promote whole subtrees via nested matches — that was parking
    // Boolean*/Body* full silhouettes into staticGroup.
    const roots = [...model.children];
    const bag =
      roots.length === 1 && /crowblaster/i.test(roots[0].name || "")
        ? [...roots[0].children]
        : roots;

    function countMeshes(rootObj) {
      let n = 0;
      rootObj.traverse((o) => {
        if (o.isMesh) n += 1;
      });
      return n;
    }

    const groupLog = { static: [], pan: [], tilt: [], stripped: doomed.length };

    for (const part of bag) {
      // Second pass: anything still not on the keep list is a leftover ghost
      if (shouldStrip(part.name) || (!isKeep(part.name) && !part.children?.length)) {
        part.parent?.remove(part);
        disposeObject(part);
        continue;
      }
      // If a keep parent somehow still nests history, strip nested leftovers
      const nestedDoomed = [];
      part.traverse((o) => {
        if (o === part) return;
        if (shouldStrip(o.name)) nestedDoomed.push(o);
      });
      nestedDoomed.reverse();
      for (const o of nestedDoomed) {
        o.parent?.remove(o);
        disposeObject(o);
      }

      const kind = classifyPart(part.name);
      const meshN = countMeshes(part);
      if (kind === "static") {
        staticGroup.attach(part);
        groupLog.static.push(`${part.name}(${meshN})`);
      } else if (kind === "tilt") {
        tiltGroup.attach(part);
        groupLog.tilt.push(`${part.name}(${meshN})`);
      } else {
        panGroup.attach(part);
        groupLog.pan.push(`${part.name}(${meshN})`);
      }
    }
    if (model.parent) model.parent.remove(model);

    // Belt-and-suspenders: anything still in staticGroup should pan with arms
    for (const child of [...staticGroup.children]) {
      panGroup.attach(child);
      groupLog.pan.push(`fromStatic:${child.name}`);
    }

    tiltGroup.traverse((o) => {
      if (/sprayholebinder/i.test(o.name || "")) sprayHole = o;
    });

    // Dome now lives in panGroup — ensure inner stripped + mint glass on remaining shell
    stripDomeInnerShells(panGroup);
    const domeGroup = panGroup.getObjectByName("Dome");
    if (domeGroup && !domeGroup.getObjectByName("Dome_shell")) {
      mergeDomeOuterShell(domeGroup);
    } else if (!panGroup.getObjectByName("Dome_shell")) {
      mergeDomeOuterShell(panGroup);
    }
    panGroup.traverse((o) => {
      if (!o.isMesh) return;
      if (!isDomeMeshName(o.name) && o.name !== "Dome_shell") return;
      o.material = makeDomeGlassMaterial();
      o.renderOrder = 3;
    });


    // Center in file space, then apply CAD→Y-up orientation once
    turretRoot.updateMatrixWorld(true);
    const localBox = new THREE.Box3().setFromObject(turretRoot);
    const localCenter = localBox.getCenter(new THREE.Vector3());
    turretRoot.position.sub(localCenter);

    scene.remove(turretRoot);
    orientRoot.add(turretRoot);

    orientRoot.updateMatrixWorld(true);
    const worldBox = new THREE.Box3().setFromObject(orientRoot);
    orientRoot.position.y -= worldBox.min.y + 0.02;
    orientRoot.position.x -= (worldBox.min.x + worldBox.max.x) * 0.5;
    orientRoot.position.z -= (worldBox.min.z + worldBox.max.z) * 0.5;

    const coreWorld = new THREE.Vector3();
    panPivot.getWorldPosition(coreWorld);
    core.position.copy(coreWorld);
    core.position.y += 0.04;

    modelReady = true;
    setReadout("scan", "ready");
    window.__gwbReady = true;
    const sb = new THREE.Box3().setFromObject(staticGroup);
    const tb = new THREE.Box3().setFromObject(tiltGroup);
    const pb = new THREE.Box3().setFromObject(panGroup);
    console.info("[gwb-turret] ready", {
      static: staticGroup.children.length,
      pan: panGroup.children.length,
      tilt: tiltGroup.children.length,
      staticParts: groupLog.static,
      panParts: groupLog.pan,
      tiltParts: groupLog.tilt,
      strippedNodes: groupLog.stripped,
      strippedDomeInner,
      mergedDomeShell,
      staticSize: sb.getSize(new THREE.Vector3()).toArray(),
      panSize: pb.getSize(new THREE.Vector3()).toArray(),
      tiltSize: tb.getSize(new THREE.Vector3()).toArray(),
      orientEuler: [orientRoot.rotation.x, orientRoot.rotation.y, orientRoot.rotation.z],
    });
    window.__gwbGroups = groupLog;
    window.__gwbDebug = {
      staticGroup,
      panGroup,
      tiltGroup,
      panPivot,
      tiltPivot,
      orientRoot,
      turretRoot,
      scene,
    };
  } catch (err) {
    console.warn("[gwb-turret] GLB load failed", err);
    setReadout("offline", "—");
  }

  function resize() {
    const w = el.clientWidth || 1;
    const h = el.clientHeight || 1;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(el);

  let birdIndex = 0;
  let phase = "seek";
  let phaseT = 0;
  let targetPan = 0;
  let targetTilt = 0;
  let waterT = 0;
  const sprayWorld = new THREE.Vector3();
  const birdWorld = new THREE.Vector3();
  const pivotWorld = new THREE.Vector3();
  const tmp = new THREE.Vector3();

  function updateBird(bird, t) {
    const a = bird.userData.phase + t * bird.userData.speed;
    const r = bird.userData.radius;
    // Gentle bob / glide — no aggressive wing flap
    const y =
      bird.userData.height +
      Math.sin(t * bird.userData.bob + bird.userData.phase) * 0.02;
    bird.position.set(Math.cos(a) * r, y, Math.sin(a) * r);
    bird.rotation.order = "YXZ";
    bird.rotation.y = -a + Math.PI / 2;
    bird.rotation.x = Math.sin(t * bird.userData.bob * 0.7 + bird.userData.phase) * 0.08;
    bird.rotation.z = Math.sin(t * 1.6 + bird.userData.phase) * 0.05;
  }

  function setWater(visible, from, to, u) {
    waterLine.material.opacity = visible ? 0.85 : 0;
    droplets.material.opacity = visible ? 0.95 : 0;
    if (!visible) return;
    const pos = waterGeo.attributes.position.array;
    pos[0] = from.x;
    pos[1] = from.y;
    pos[2] = from.z;
    pos[3] = to.x;
    pos[4] = to.y;
    pos[5] = to.z;
    waterGeo.attributes.position.needsUpdate = true;
    for (let i = 0; i < dropletCount; i++) {
      const tt = (i / dropletCount + u) % 1;
      const jx = Math.sin(i * 12.1 + u * 20) * 0.004;
      const jy = Math.cos(i * 7.7 + u * 18) * 0.004;
      dropletPos[i * 3] = from.x + (to.x - from.x) * tt + jx;
      dropletPos[i * 3 + 1] = from.y + (to.y - from.y) * tt + jy;
      dropletPos[i * 3 + 2] = from.z + (to.z - from.z) * tt;
    }
    dropletGeo.attributes.position.needsUpdate = true;
  }

  function computeAim(bird) {
    bird.getWorldPosition(birdWorld);
    panPivot.getWorldPosition(pivotWorld);
    // Azimuth in world XZ (upright). panPivot.rotation.z maps 1:1 to this yaw.
    targetPan = Math.atan2(birdWorld.x - pivotWorld.x, birdWorld.z - pivotWorld.z);
    const dx = birdWorld.x - pivotWorld.x;
    const dy = birdWorld.y - pivotWorld.y;
    const dz = birdWorld.z - pivotWorld.z;
    const horiz = Math.hypot(dx, dz) || 1e-6;
    // Clamp tilt so mechanism stays readable / doesn't tumble
    targetTilt = THREE.MathUtils.clamp(Math.atan2(dy, horiz), -0.45, 0.55);
  }

  function updateSprayOrigin() {
    if (sprayHole) sprayHole.getWorldPosition(sprayWorld);
    else sprayMarker.getWorldPosition(sprayWorld);
  }

  const clock = new THREE.Clock();
  let raf = 0;

  function frame() {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(clock.getDelta(), 0.05);
    const t = clock.elapsedTime;

    birds.forEach((b) => updateBird(b, t));

    if (modelReady && !reduced) {
      const bird = birds[birdIndex];
      computeAim(bird);

      if (phase === "seek") {
        // Moderate slew — slower dome pan/tilt (Ford 2026-09-28); still locks
        // under bird orbit with a longer seek timeout.
        setReadout("acquire", "armed");
        phaseT += dt;
        const dp = shortestAngle(panPivot.rotation.z, targetPan);
        const dtlt = shortestAngle(tiltPivot.rotation.y, targetTilt);
        panPivot.rotation.z += dp * Math.min(1, dt * 7.5);
        tiltPivot.rotation.y += dtlt * Math.min(1, dt * 5.5);
        const locked = Math.abs(dp) < 0.07 && Math.abs(dtlt) < 0.09;
        // Timeout keeps the aim→spray→next cycle alive forever even if a
        // pathological frame never crosses the lock epsilon.
        if (locked || phaseT > 2.2) {
          phase = "hold";
          phaseT = 0;
        }
      } else if (phase === "hold") {
        panPivot.rotation.z = targetPan;
        tiltPivot.rotation.y = targetTilt;
        setReadout("locked", "armed");
        phaseT += dt;
        if (phaseT > 0.35) {
          phase = "fire";
          phaseT = 0;
          waterT = 0;
        }
      } else if (phase === "fire") {
        panPivot.rotation.z = targetPan;
        tiltPivot.rotation.y = targetTilt;
        setReadout("locked", "FIRE");
        waterT += dt;
        updateSprayOrigin();
        setWater(true, sprayWorld, birdWorld, waterT * 2.5);
        if (waterT > 1.05) {
          setWater(false);
          phase = "next";
          phaseT = 0;
        }
      } else if (phase === "next") {
        setReadout("scan", "ready");
        phaseT += dt;
        // Idle: gentle pan drift, settle tilt toward slight up
        panPivot.rotation.z += dt * 0.14;
        // Keep pan in [-π, π] so drift never accumulates without bound
        if (panPivot.rotation.z > Math.PI) panPivot.rotation.z -= Math.PI * 2;
        if (panPivot.rotation.z < -Math.PI) panPivot.rotation.z += Math.PI * 2;
        tiltPivot.rotation.y += (0.08 - tiltPivot.rotation.y) * Math.min(1, dt * 1.5);
        if (phaseT > 0.7) {
          birdIndex = (birdIndex + 1) % birds.length;
          phase = "seek";
          phaseT = 0;
        }
      }
    } else if (modelReady && reduced) {
      panPivot.rotation.z = 0.35;
      tiltPivot.rotation.y = 0.12;
      setWater(false);
      setReadout("idle", "safe");
    } else {
      setWater(false);
    }

    renderer.render(scene, camera);
  }

  frame();

  el._gwbDispose = () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    draco.dispose();
    renderer.dispose();
  };
}

function initAll() {
  document.querySelectorAll("[data-gwb-turret]").forEach((el) => {
    if (el.dataset.booted) return;
    el.dataset.booted = "1";
    boot(el);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll);
} else {
  initAll();
}

/* ghost-strip v6 2026-09-28 — dome mint FrontSide + strip inner shells/cap */
